import React, { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  twitterCard?: 'summary' | 'summary_large_image';
}

/**
 * Componente para gestionar metadatos SEO y Open Graph
 */
const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description = 'Domired - La red social para compartir y conectar',
  image = 'https://domired.top/logo512.png',
  url = typeof window !== 'undefined' ? window.location.href : 'https://domired.top',
  type = 'website',
  twitterCard = 'summary_large_image'
}) => {
  // Asegurarse de que el título tenga el nombre del sitio
  const fullTitle = title.includes('Domired') ? title : `${title} | Domired`;
  
  useEffect(() => {
    // Actualizar el título del documento
    document.title = fullTitle;
    
    // Actualizar la descripción
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', description);
      document.head.appendChild(metaDescription);
    }
    
    // Actualizar la URL canónica
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (linkCanonical) {
      linkCanonical.setAttribute('href', url);
    } else {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      linkCanonical.setAttribute('href', url);
      document.head.appendChild(linkCanonical);
    }
    
    // Función para actualizar o crear meta tags
    const updateMetaTag = (property: string, content: string, isOg = false) => {
      const selector = isOg 
        ? `meta[property="${property}"]` 
        : `meta[name="${property}"]`;
      
      let meta = document.querySelector(selector);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        if (isOg) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };
    
    // Open Graph
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:site_name', 'Domired', true);
    
    // Twitter Card
    updateMetaTag('twitter:card', twitterCard);
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    
    // Otros metadatos
    updateMetaTag('application-name', 'Domired');
    updateMetaTag('apple-mobile-web-app-title', 'Domired');
    updateMetaTag('theme-color', '#ef4444');
    
  }, [title, description, image, url, type, twitterCard, fullTitle]);
  
  // Este componente no renderiza nada visible
  return null;
};

export default SEOHead;
